import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Avatar, Button, Spin, Timeline } from "antd";
import QuestionContainer from "../../../components/questionContainer/QuestionContainer.jsx";
import LoadableButton from "../../../components/button/LoadableButton.jsx";
import {
  GetSectionListApi,
  GetSectionWiseQuestionApi,
  SubmitFileApi,
  RepeatSectionApi,
  RemoveSectionApi,
} from "../../../api/request/service.js";

const QuestionAnswerFlow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [file, setFile] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const [sectionList, setSectionList] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [isCompleted, setIsCompleted] = useState(true);
  const [isNext, setIsNext] = useState(true);
  const [isPrevious, setPrevious] = useState(true);
  const [isAllowSectionRepeat, setIsAllowSectionRepeat] = useState(false);
  const [isPrimarySection, setIsPrimarySection] = useState(false);
  const [isLoadingRepeat, setIsLoadingRepeat] = useState(false);
  const [isInitial, setIsInitial] = useState(true);
  const [isLoadingRemoveSection, setIsLoadingRemoveSection] = useState(false);

  const getFileDetails = async (isLoad = true) => {
    try {
      if (isLoad) setIsLoading(true);
      const { data } = await GetSectionListApi({ file_id: id });
      if (data?.status) {
        setFile(data?.file_details);
        setSectionList(data?.data);
        if (isInitial) {
          setSelectedSection(data?.data[0]);
          setIsInitial(false);
          if (data?.data?.length > 1) {
            setIsNext(true);
            setPrevious(false);
          } else {
            setIsNext(false);
            setPrevious(true);
          }
        }
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getFileDetails();
  }, [id]);

  const getSectionWiseQuestion = async (isLoad = true) => {
    if (!selectedSection) return;

    try {
      if (isLoad) setIsQuestionLoading(true);
      let params = {
        file_id: id,
        section_id: selectedSection?.section_id,
      };
      const { data } = await GetSectionWiseQuestionApi(params);
      if (data?.status) {
        setQuestions(data?.responses);
        setIsAllowSectionRepeat(data?.section?.allow_repeat);
        setIsPrimarySection(data?.section?.is_primary);
      }
      setIsQuestionLoading(false);
    } catch (error) {
      setIsQuestionLoading(false);
    }
  };

  useEffect(() => {
    getSectionWiseQuestion();
  }, [selectedSection]);

  const handleNextStep = () => {
    const getNextSection = (sectionId) => {
      const index = sectionList.findIndex(
        (section) => section.section_id === sectionId
      );

      for (let i = index + 1; i < sectionList.length; i++) {
        if (sectionList[i].is_show === 1) return sectionList[i];
      }
      return null;
    };

    const nextSection = getNextSection(selectedSection?.section_id);
    if (nextSection) {
      setSelectedSection(nextSection);
      setPrevious(true);
    }
    const lastSection = sectionList[sectionList?.length - 1];
    setIsNext(lastSection?.section_id !== nextSection?.section_id);
  };

  const handlePreviousStep = () => {
    const getPreviousSection = (sectionId) => {
      const index = sectionList.findIndex(
        (section) => section.section_id === sectionId
      );

      for (let i = index - 1; i >= 0; i--) {
        if (sectionList[i].is_show === 1) return sectionList[i];
      }
      return null;
    };

    const previousSection = getPreviousSection(selectedSection?.section_id);
    if (previousSection) setSelectedSection(previousSection);

    const firstSection = sectionList[0];
    setPrevious(firstSection?.section_id !== previousSection?.section_id);
    setIsNext(true);
  };

  const handleSubmit = async () => {
    try {
      setIsLoadingSubmit(true);
      let params = { file_id: id };
      const { data } = await SubmitFileApi(params);
      if (data?.status) {
        navigate("/files", { state: { status: "inprocess" } });
      }
      setIsLoadingSubmit(false);
    } catch (error) {
      setSelectedSection(
        sectionList.find(
          (obj) =>
            obj.section_id === Object.keys(error?.response?.data?.data)[0]
        ) || null
      );
      toast.error("Please complete this section first.");
      setIsLoadingSubmit(false);
    }
  };

  const handleSectionChange = (section) => {
    setSelectedSection(section);

    const firstSection = sectionList[0];
    const lastSection = sectionList[sectionList.length - 1];

    setPrevious(firstSection?.section_id !== section?.section_id);
    setIsNext(lastSection?.section_id !== section?.section_id);
  };

  const handleRepeatSection = async () => {
    try {
      setIsLoadingRepeat(true);
      let params = {
        file_id: id,
        service_section_id: selectedSection?.section_id,
      };
      const { data } = await RepeatSectionApi(params);
      if (data?.status) getFileDetails(false);
      setIsLoadingRepeat(false);
    } catch (error) {
      setIsLoadingRepeat(false);
    }
  };

  const handleRemoveSection = async () => {
    try {
      setIsLoadingRemoveSection(true);
      let params = {
        service_section_id: selectedSection?.section_id,
      };
      const { data } = await RemoveSectionApi(params);
      if (data?.status) {
        getFileDetails(false);
        handleNextStep();
        handlePreviousStep();
      }
      setIsLoadingRemoveSection(false);
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message);
      }
      setIsLoadingRemoveSection(false);
    }
  };

  return (
    <div className="grid xl:grid-cols-4 gap-4">
      <div className="border rounded-lg pt-3 bg-white">
        <p className=" text-primary text-center font-semibold border-b pb-3 capitalize truncate mx-2">
          {file?.service_name_slug || "--"}
        </p>
        <div className="px-6 pt-8 h-[calc(100vh-160px)] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center gap-2 justify-center h-full">
              <Spin />
              <p className="text-secondaryLight1">Loading...</p>
            </div>
          ) : (
            <Timeline
              className="custom-timeline"
              items={sectionList
                ?.filter((item) => item?.is_show === 1)
                ?.map((item, index) => {
                  const active =
                    selectedSection?.section_id === item?.section_id;
                  const complete =
                    item?.question_count === item?.filled_answers_count;

                  return {
                    dot: (
                      <Avatar
                        className={`cursor-pointer border font-bold ${
                          complete
                            ? "border-green-600 bg-green-600 text-white"
                            : active
                            ? "border-primary bg-primary text-white"
                            : "border-primary bg-white text-primary"
                        }`}
                      >
                        {index + 1}
                      </Avatar>
                    ),
                    children: (
                      <div
                        className={`ms-1 mb-2 hover:bg-gray-100 p-1 ps-2 rounded-md cursor-pointer ${
                          active ? "!bg-secondaryLight" : ""
                        }`}
                        onClick={() => handleSectionChange(item)}
                      >
                        <p className="text-slate-600">
                          Question{" "}
                          <span className="text-gray-900 font-semibold">
                            {item?.filled_answers_count}
                          </span>
                          /
                          <span className="text-gray-900 font-semibold">
                            {item?.question_count}
                          </span>
                        </p>
                        <p className="font-bold">{item?.section_name}</p>
                      </div>
                    ),
                  };
                })}
            />
          )}
        </div>
      </div>

      {selectedSection && (
        <div className="xl:col-span-3 border rounded-lg p-3 bg-white">
          {isQuestionLoading ? (
            <div className="flex flex-col items-center justify-center gap-2 h-screen">
              <Spin />
              <p className="text-secondaryLight1">Loading...</p>
            </div>
          ) : (
            <div>
              <QuestionContainer
                questions={questions}
                reloadSections={getFileDetails}
                reloadQuestions={getSectionWiseQuestion}
              />

              {isAllowSectionRepeat === 1 && (
                <div className="flex gap-5 items-center">
                  <LoadableButton
                    className="mt-8"
                    type="button"
                    lable="Repeat Section"
                    loadingLable="Loading section..."
                    onClick={handleRepeatSection}
                    isLoading={isLoadingRepeat}
                  />
                  {isPrimarySection === 0 ? (
                    <LoadableButton
                      className="mt-8"
                      type="button"
                      lable="Remove Section"
                      loadingLable="Removing section..."
                      onClick={handleRemoveSection}
                      isLoading={isLoadingRemoveSection}
                    />
                  ) : null}
                </div>
              )}

              <div className="question-container w-4/5">
                <div className="mt-8 mb-6">
                  <div className="flex items-center gap-4">
                    {isPrevious && (
                      <button
                        className="btn-primary flex items-center gap-2"
                        onClick={handlePreviousStep}
                      >
                        <img
                          src="/assets/icons/arrow-right.svg"
                          className="size-4 rotate-180"
                          alt="icon"
                        />
                        Previous
                      </button>
                    )}
                    {isNext && (
                      <button
                        className="btn-primary flex items-center gap-2"
                        onClick={handleNextStep}
                      >
                        Next
                        <img
                          src="/assets/icons/arrow-right.svg"
                          className="size-4"
                          alt="icon"
                        />
                      </button>
                    )}
                    {(file?.is_submit === 0 || file?.unsubmit === 1) && (
                      <LoadableButton
                        lable="Submit"
                        loadingLable="Submitting..."
                        onClick={handleSubmit}
                        isLoading={isLoadingSubmit}
                      />
                    )}
                    <div className="flex items-center gap-1">
                      <div className="size-[22px]">
                        {isCompleted ? (
                          <img
                            className="size-6"
                            src="/assets/icons/check-circle.svg"
                            alt="icon"
                          />
                        ) : (
                          <img
                            className="size-[22px]"
                            src="/assets/icons/x-circle.svg"
                            alt="icon"
                          />
                        )}
                      </div>
                      <p className="text-secondaryLight1">
                        {`All answers are ${isCompleted ? "" : "not"} saved.`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuestionAnswerFlow;
